import SridSelector from '@/components/SridSelector';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { execute } from '@/graphql/execute';
import type { GetProjectQuery, Project } from '@/graphql/graphql';
import PageSkeleton from '@/layout/Skeleton';
import { deleteProjectMutation, updateProjectMutation } from '@/mutations/project.mutation';
import { getProjectQuery } from '@/queries/project.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { Loader } from 'lucide-react';
import posthog from 'posthog-js';
import { FunctionComponent, useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface iProjectEditProps {}

const ProjectEdit: FunctionComponent<iProjectEditProps> = () => {
  const { projectId } = useParams<{ projectId: string }>();
  if (!projectId) throw new Error('Project ID is required');

  const {
    project: initialProject,
    projectLoading,
    href,
  } = useOutletContext<{
    project: Project;
    projectLoading: boolean;
    projectError: Error | null;
    href: string;
  }>();
  const { data: projectData, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => execute(getProjectQuery, { id: projectId! }),
  });
  const project = projectData?.project;

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className='w-full h-full flex items-center justify-center'>
          <Loader className='w-4 h-4 animate-spin' />
        </div>
      );
    }
    if (!project) return <div>Project not found</div>;
    return <ProjectForm project={project} />;
  }, [isLoading, project, href]);

  return (
    <PageSkeleton
      steps={
        projectLoading
          ? [{ label: 'Loading...', href }]
          : [
              { label: initialProject.name, href },
              {
                label: 'Edit Project',
                href: `${href}/edit`,
              },
            ]
      }>
      {content}
    </PageSkeleton>
  );
};

const ProjectForm = ({ project }: { project: GetProjectQuery['project'] }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      name: project.name,
      description: project.description ?? '',
      srid: project.srid,
      initialPosition: project.initialPosition,
      initialAngle: project.initialAngle,
    },
    onSubmit: async (values, helpers) => {
      const id = toast.loading('Updating project...');
      try {
        if (values.initialPosition) values.initialPosition.srid = values.srid;
        await execute(updateProjectMutation, { input: { id: project.id, ...values } });
        helpers.resetForm({
          values: {
            name: values.name,
            description: values.description,
            srid: values.srid,
            initialPosition: values.initialPosition,
            initialAngle: values.initialAngle,
          },
        });
        queryClient.invalidateQueries({ queryKey: ['project', project.id] });
        queryClient.invalidateQueries({ queryKey: ['projects', project.organizationId] });
        toast.success('Project updated', { id });
        posthog.capture('Project Updated');
      } catch (error) {
        toast.error('Failed to update project', { id });
      }
    },
  });

  const _handleDelete = async () => {
    const id = toast.loading('Deleting project...');
    posthog.capture('Project Deleted');
    try {
      await execute(deleteProjectMutation, { id: project.id });
      navigate('/', { replace: true });
      queryClient.invalidateQueries({ queryKey: ['projects', project.organizationId] });
      toast.success('Project deleted', { id });
    } catch (error) {
      toast.error('Failed to delete project', { id });
    }
  };
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className='flex flex-col gap-4 max-w-md p-4 mx-auto'>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-name'>Project Name</Label>
          <Input id='project-name' placeholder='Untitled Project' {...formik.getFieldProps('name')} />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-description'>Project Description</Label>
          <Textarea
            id='project-description'
            placeholder='Project Description'
            {...formik.getFieldProps('description')}
          />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-srid'>EPSG (SRID – Spatial Reference System Identifier)</Label>
          <SridSelector
            className='w-full'
            value={formik.values.srid.toString()}
            onChange={(value) => formik.setFieldValue('srid', parseInt(value))}
          />
        </div>
        <div className='grid w-full max-w-md items-center gap-3'>
          <Label htmlFor='project-srid'>Initial Position</Label>
          <div className='grid grid-cols-3 gap-2'>
            <Input type='number' placeholder='X' {...formik.getFieldProps('initialPosition.coords[0]')} />
            <Input type='number' placeholder='Y' {...formik.getFieldProps('initialPosition.coords[1]')} />
            <Input type='number' placeholder='Z' {...formik.getFieldProps('initialPosition.coords[2]')} />
          </div>
          <Label htmlFor='project-srid'>Initial Angle (0° for North, 90° for East)</Label>
          <Input type='number' placeholder='0°' {...formik.getFieldProps('initialAngle')} />
        </div>
        <Button disabled={formik.isSubmitting || !formik.dirty} type='submit' className='w-full'>
          Submit
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant='destructive'>Delete Project</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete this project and remove all data from our
                servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={_handleDelete}>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </form>
  );
};

export default ProjectEdit;
